/**
===================================================
Module: frontend.src.components.SessionDetails.tsx
Description: Session details display component
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Session } from "../types";
import { VideoPreview } from "./VideoPreview";
import { apiService } from "../services/api";
import { Notification } from "./NotificationSystem";
import NotificationSystem from "./NotificationSystem";
import "../styles/SessionDetails.css";

interface SessionDetailsProps {
  session: Session;
  onClose: () => void;
  onUpdate?: (
    sessionId: string,
    updates: { name: string; description: string },
  ) => void;
}

const SessionDetails: React.FC<SessionDetailsProps> = ({
  session,
  onClose,
  onUpdate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(session.name || "");
  const [description, setDescription] = useState(session.description || "");
  const [isUpdating, setIsUpdating] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [currentSession, setCurrentSession] = useState<Session>(session);
  const [videoUrl, setVideoUrl] = useState<string>("");
  const [isLoadingVideo, setIsLoadingVideo] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const isRefreshing = useRef(false);

  // Function to fetch video URL
  const fetchVideoUrl = useCallback(async () => {
    try {
      setIsLoadingVideo(true);
      const response = await apiService.getSessionInputVideo(session.id);
      setVideoUrl(response.download_url);
    } catch (error) {
      addNotification("error", "動画のロードに失敗しました。時間をおいてもう一度お試しください");
    } finally {
      setIsLoadingVideo(false);
      isRefreshing.current = false;
    }
  }, [session.id]);

  useEffect(() => {
    fetchVideoUrl();
  }, [fetchVideoUrl]);

  // Handle video URL expiration (403 error) - refresh video URL
  const handleVideoError = useCallback(
    async (error: any) => {
      // Prevent infinite refresh loops
      if (isRefreshing.current) {
        return;
      }

      // Check if it's a 403 error (URL expired)
      if (
        error?.status === 403 ||
        error?.message?.includes("403") ||
        error?.message?.includes("expired")
      ) {
        isRefreshing.current = true;
        addNotification("info", "動画を再ロードしています");

        // Force a new video URL fetch
        await fetchVideoUrl();

        // Update refresh key to force VideoPreview to re-mount with new URL
        setRefreshKey((prev) => prev + 1);
      }
    },
    [fetchVideoUrl],
  );

  const addNotification = (
    type: "success" | "error" | "info",
    message: string,
  ) => {
    const newNotification: Notification = {
      id: Date.now().toString(),
      type,
      message,
      duration: 5000,
    };
    setNotifications((prev) => [...prev, newNotification]);
  };

  const removeNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const handleSave = async () => {
    if (name.trim() === "") {
      addNotification("error", "ファイル名は入力必須");
      return;
    }

    setIsUpdating(true);
    try {
      // Call the API directly
      const updatedSession = await apiService.updateSessionDetails(session.id, {
        name: name.trim(),
        description: description.trim(),
      });

      // Update the local session data
      setCurrentSession(updatedSession);

      // Call the optional callback if provided (for parent components that want to refresh)
      if (onUpdate) {
        await onUpdate(session.id, {
          name: name.trim(),
          description: description.trim(),
        });
      }

      setIsEditing(false);
      addNotification("success", "ファイルの更新が完了しました");
    } catch (error) {
      addNotification("error", "ファイルの更新に失敗しました。時間をおいてもう一度お試しください");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancel = () => {
    setName(currentSession.name || "");
    setDescription(currentSession.description || "");
    setIsEditing(false);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleResultsClick = () => {
    // Open Visualiser in a new tab
    const visualiserUrl = `/results/${session.id}`;
    window.open(visualiserUrl, "_blank");
  };

  return (
    <>
      <NotificationSystem
        notifications={notifications}
        onRemove={removeNotification}
      />

      <div className="session-details-overlay" onClick={handleOverlayClick}>
        <div
          className="session-details-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="session-details-header">
            <button
              className="close-button"
              onClick={onClose}
              aria-label="Close session details"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <h2 className="session-details-title">ファイルの詳細</h2>

            <button
              className="results-button"
              onClick={handleResultsClick}
              aria-label="Open results in new tab"
            >
              結果を表示
            </button>
          </div>

          <div className="session-details-body">
            <div className="video-section">
              <h3>動画プレビュー</h3>
              <div className="video-container">
                {isLoadingVideo ? (
                  <div className="video-loading">
                    <div className="spinner"></div>
                    <span>ロード中...</span>
                  </div>
                ) : videoUrl ? (
                  <VideoPreview
                    key={`${refreshKey}-${videoUrl}`}
                    srcURL={videoUrl}
                    controls={true}
                    className="video-player"
                    enable360={true}
                    onError={handleVideoError}
                  />
                ) : (
                  <div className="video-error">
                    <span>動画のロードに失敗しました</span>
                  </div>
                )}
              </div>
            </div>

            <div className="session-info-section">
              <div className="session-info-header">
                <h3>ファイル情報</h3>
                <button
                  className="edit-button"
                  onClick={() => setIsEditing(!isEditing)}
                  disabled={isUpdating}
                >
                  {isEditing ? "キャンセル" : "編集"}
                </button>
              </div>

              <div className="session-info-content">
                <div className="info-field">
                  <label className="info-label">名前:</label>
                  {isEditing ? (
                    <input
                      type="text"
                      className="info-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="ファイル名を入力してください"
                    />
                  ) : (
                    <span className="info-value">
                      {currentSession.name || " "}
                    </span>
                  )}
                </div>

                <div className="info-field">
                  <label className="info-label">説明:</label>
                  {isEditing ? (
                    <textarea
                      className="info-textarea"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="ファイルの説明を入力してください"
                      rows={3}
                    />
                  ) : (
                    <span className="info-value">
                      {currentSession.description || " "}
                    </span>
                  )}
                </div>
              </div>

              {isEditing && (
                <div className="edit-actions">
                  <button
                    className="save-button"
                    onClick={handleSave}
                    disabled={isUpdating || name.trim() === ""}
                  >
                    {isUpdating ? "保存中..." : "変更を保存"}
                  </button>
                  <button
                    className="cancel-button"
                    onClick={handleCancel}
                    disabled={isUpdating}
                  >
                    キャンセル
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SessionDetails;
